import {
  BufferGeometry,
  Group,
  InstancedMesh,
  Material,
  Mesh,
  Object3D,
  Scene,
  SkinnedMesh,
} from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

function isMeshLike(obj: Object3D): obj is Mesh {
  return (obj as Mesh).isMesh === true;
}

function normalizeMaterials(material: Material | Material[]): Material[] {
  return Array.isArray(material) ? material : [material];
}

function isUnderNamedAncestor(mesh: Object3D, names: Set<string>): boolean {
  let p: Object3D | null = mesh.parent;
  while (p) {
    if (names.has(p.name)) return true;
    p = p.parent;
  }
  return false;
}

function isTransformControlsSubtree(mesh: Object3D): boolean {
  let p: Object3D | null = mesh;
  while (p) {
    const t = p as unknown as { isTransformControls?: boolean };
    if (t.isTransformControls === true) return true;
    p = p.parent;
  }
  return false;
}

const SKIP_ANCESTOR_NAMES = new Set(["engine-debug-log"]);

const SKIP_MESH_NAMES = new Set([
  "sugar-scene-axes-helper",
  "sugar-configurator-axes",
]);

/**
 * WebGL için ayrı `Scene`: mesh’leri kopyalamadan, kare başına materyal gruplarına
 * `attach` / sonra eski parent’a geri alır. CSS2D/CSS3D logical sahneye kalır.
 */
export class RenderSceneReparentManager {
  private readonly renderScene: Scene;
  private lastTopologySig = "";
  private materialGroups = new Map<string, Group>();
  private meshesFlat: Mesh[] = [];
  private readonly restoreStack: {
    mesh: Mesh;
    parent: Object3D;
    index: number;
  }[] = [];
  /** Aynı materyal için `mergeGeometries` ile tek draw call (world matrisi geometriye gömülü). */
  private mergeMeshesPerMaterial = false;
  private _mergedMeshes: Mesh[] = [];

  constructor(renderScene: Scene) {
    this.renderScene = renderScene;
    this.renderScene.name = "engine-scene-to-render";
  }

  getScene(): Scene {
    return this.renderScene;
  }

  setMergeMeshesPerMaterial(value: boolean): void {
    this.mergeMeshesPerMaterial = value;
    this.lastTopologySig = "";
  }

  get mergeMeshesPerMaterialEnabled(): boolean {
    return this.mergeMeshesPerMaterial;
  }

  private _disposeMergedMeshes(): void {
    for (const m of this._mergedMeshes) {
      m.geometry?.dispose();
      m.removeFromParent();
    }
    this._mergedMeshes = [];
  }

  /**
   * Topoloji imzası veya bucket kümesi değiştiyse materyal gruplarını ve mesh listesini yeniden kurar.
   * @returns `true` sahne grupları ve mesh listesi bu çağrıda yeniden kurulduysa.
   */
  syncStructure(
    logicalScene: Scene,
    structureSignature: string,
    buckets: ReadonlyMap<Material, readonly unknown[]>,
  ): boolean {
    if (
      structureSignature === this.lastTopologySig &&
      this.materialGroups.size > 0
    ) {
      return false;
    }
    this.lastTopologySig = structureSignature;

    this._disposeMergedMeshes();
    while (this.renderScene.children.length > 0) {
      this.renderScene.remove(this.renderScene.children[0]);
    }
    this.materialGroups.clear();
    this.meshesFlat = [];

    logicalScene.updateWorldMatrix(true, true);
    const meshes: Mesh[] = [];
    logicalScene.traverse((obj) => {
      if (!isMeshLike(obj)) return;
      const mesh = obj as Mesh;
      if (SKIP_MESH_NAMES.has(mesh.name)) return;
      if (isUnderNamedAncestor(mesh, SKIP_ANCESTOR_NAMES)) return;
      if (isTransformControlsSubtree(mesh)) return;
      const mats = normalizeMaterials(mesh.material).filter(Boolean);
      if (mats.length === 0) return;
      meshes.push(mesh);
    });
    meshes.sort((a, b) => a.id - b.id);

    if (this.mergeMeshesPerMaterial) {
      this._syncStructureMerged(meshes);
    } else {
      const matUuids = [...buckets.keys()]
        .map((m) => m.uuid)
        .filter(Boolean)
        .sort();
      for (const uid of matUuids) {
        const g = new Group();
        g.name = `render-mat-${uid}`;
        this.materialGroups.set(uid, g);
        this.renderScene.add(g);
      }
      this.meshesFlat = meshes;
    }
    return true;
  }

  /**
   * Materyal başına bir birleşik `Mesh` (+ gerekirse skinned/merge hatası için reparent listesi).
   */
  private _syncStructureMerged(allMeshes: Mesh[]): void {
    const byUuid = new Map<string, { material: Material; meshes: Mesh[] }>();
    for (const mesh of allMeshes) {
      const mats = normalizeMaterials(mesh.material).filter(Boolean);
      const mat = mats[0]!;
      const uid = mat.uuid;
      let bucket = byUuid.get(uid);
      if (!bucket) {
        bucket = { material: mat, meshes: [] };
        byUuid.set(uid, bucket);
      }
      bucket.meshes.push(mesh);
    }

    const sortedUuids = [...byUuid.keys()].sort();

    for (const uid of sortedUuids) {
      const { material, meshes: groupMeshes } = byUuid.get(uid)!;
      const g = new Group();
      g.name = `render-mat-${uid}`;
      this.materialGroups.set(uid, g);
      this.renderScene.add(g);

      const mergeable: Mesh[] = [];
      const reparentOnly: Mesh[] = [];

      for (const m of groupMeshes) {
        if (
          (m as SkinnedMesh).isSkinnedMesh === true ||
          (m as InstancedMesh).isInstancedMesh === true
        ) {
          reparentOnly.push(m);
          continue;
        }
        mergeable.push(m);
      }

      if (mergeable.length > 0) {
        const geoms: BufferGeometry[] = [];
        for (const mesh of mergeable) {
          const bg = mesh.geometry as BufferGeometry;
          if (!bg?.attributes?.position) continue;
          const cloned = bg.clone();
          cloned.applyMatrix4(mesh.matrixWorld);
          geoms.push(cloned);
        }
        if (geoms.length > 0) {
          const merged = mergeGeometries(geoms, false);
          for (const x of geoms) x.dispose();
          if (merged) {
            const combined = new Mesh(merged, material);
            combined.name = `merged-mat-${uid}`;
            combined.frustumCulled = false;
            g.add(combined);
            this._mergedMeshes.push(combined);
          } else {
            reparentOnly.push(...mergeable);
          }
        } else {
          reparentOnly.push(...mergeable);
        }
      }

      for (const m of reparentOnly) this.meshesFlat.push(m);
    }
    this.meshesFlat.sort((a, b) => a.id - b.id);
  }

  syncEnvironmentFrom(logicalScene: Scene): void {
    this.renderScene.background = logicalScene.background;
    this.renderScene.environment = logicalScene.environment;
    this.renderScene.fog = logicalScene.fog;
    this.renderScene.environmentRotation.copy(logicalScene.environmentRotation);
    this.renderScene.backgroundRotation.copy(logicalScene.backgroundRotation);
    this.renderScene.environmentIntensity = logicalScene.environmentIntensity;
    this.renderScene.backgroundIntensity = logicalScene.backgroundIntensity;
    this.renderScene.backgroundBlurriness = logicalScene.backgroundBlurriness;
  }

  pushMeshesToRenderScene(): void {
    this.restoreStack.length = 0;
    for (const mesh of this.meshesFlat) {
      const parent = mesh.parent;
      if (!parent) continue;
      const mats = normalizeMaterials(mesh.material).filter(Boolean);
      if (mats.length === 0) continue;
      const uuid = mats[0]!.uuid;
      const group = this.materialGroups.get(uuid);
      if (!group) continue;
      const idx = parent.children.indexOf(mesh);
      this.restoreStack.push({
        mesh,
        parent,
        index: idx >= 0 ? idx : parent.children.length,
      });
      group.attach(mesh);
    }
  }

  popMeshesFromRenderScene(): void {
    for (let i = this.restoreStack.length - 1; i >= 0; i--) {
      const r = this.restoreStack[i];
      r.parent.attach(r.mesh);
    }
    this.restoreStack.length = 0;
  }
}
